import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  List,
  useMediaQuery,
  Divider,
} from "@mui/material";

import { useHistory, useLocation } from "react-router";
import withAuth from "../../Hoc/withAuth";
import { useDispatch } from "react-redux";
import MuiDrawer from "@mui/material/Drawer";

import SchoolAdminDrawer   from "./Drawers/SchoolAdminDrawer";
import SchoolUserDrawer    from "./Drawers/SchoolUserDrawer";
import StudentDrawer       from "./Drawers/StudentDrawer";
import ParentDrawer        from "./Drawers/ParentDrawer";
import OrgAdminDrawer      from "./Drawers/OrgAdminDrawer";
import OrgUserDrawer       from "./Drawers/OrgUserDrawer";
import AdminDrawer         from "./Drawers/AdminDrawer";
import TeacherDrawer       from "./Drawers/TeacherDrawer";
import { getMe }           from "../../Redux/actions/Auth";
import MobileSidebarNav    from "./MobileSidebarNav";
import MobileDrawerActions from "./MobileDrawerActions";
import TopBar              from "./TopBar";
import { SidebarAccentProvider } from "./SidebarAccentContext";
import logoLight from "../../images/logos/NewLogoLight.png";
import logoDark  from "../../images/logos/NewLogoDark.png";

const TOPBAR_HEIGHT  = 60;
const drawerWidth    = 256;
const drawerMinWidth = 72;

const NavBarWDrawer = ({ setTheme, currentTheme, user, children, token }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme    = useTheme();
  // eslint-disable-next-line no-unused-vars
  const router   = useHistory();

  const isExtraLarge = useMediaQuery("(min-width:1500px)");
  const isLarge      = useMediaQuery(theme.breakpoints.up("lg"));
  const isMobile     = useMediaQuery(theme.breakpoints.down("lg"));

  const [isDrawerOpen,      setIsDrawerOpen]      = useState(isExtraLarge);
  const [isDrawerMinimized, setIsDrawerMinimized] = useState(false);

  useEffect(() => {
    if (isExtraLarge)       { setIsDrawerOpen(true);  setIsDrawerMinimized(false); }
    else if (isLarge)       { setIsDrawerOpen(true);  setIsDrawerMinimized(true);  }
    else                    { setIsDrawerOpen(false); setIsDrawerMinimized(false); }
  }, [isExtraLarge, isLarge]);

  // Close drawer on navigation (mobile). Depends only on the path so opening
  // the drawer (which flips isDrawerOpen) doesn't immediately re-close it.
  useEffect(() => {
    if (isMobile) setIsDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (token) dispatch(getMe());
  }, [dispatch, token]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setIsDrawerOpen(!isDrawerOpen);
    } else {
      if (isDrawerOpen) {
        setIsDrawerMinimized(!isDrawerMinimized);
      } else {
        setIsDrawerOpen(true);
        setIsDrawerMinimized(false);
      }
    }
  };

  const getRoleDrawerContent = () => {
    if (!user?.role) return null;
    switch (user.role) {
      case "school-admin":  return <SchoolAdminDrawer />;
      case "school-user":   return <SchoolUserDrawer />;
      case "student":       return <StudentDrawer />;
      case "parent":        return <ParentDrawer />;
      case "org-admin":     return <OrgAdminDrawer />;
      case "org-user":      return <OrgUserDrawer />;
      case "administrator": return <AdminDrawer />;
      case "teacher":       return <TeacherDrawer />;
      default:              return null;
    }
  };

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <SidebarAccentProvider minimized={false}>
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "background.default" }}>
          {user?.role && (
            <MobileSidebarNav
              setTheme={setTheme}
              currentTheme={currentTheme}
              onMenuClick={handleDrawerToggle}
            />
          )}
          {/* Mobile temporary drawer (overlay) */}
          {user?.role && (
            <MuiDrawer
              variant="temporary"
              anchor="left"
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                zIndex: (t) => t.zIndex.drawer + 3,
                "& .MuiDrawer-paper": {
                  width:           drawerWidth,
                  boxSizing:       "border-box",
                  backgroundColor: "background.paper",
                  backgroundImage: "none",
                  borderRight:     (t) => `1px solid ${t.palette.divider}`,
                  display:         "flex",
                  flexDirection:   "column",
                },
              }}
            >
              {/* Mobile brand header */}
              <Box
                onClick={() => router.push("/")}
                sx={{
                  flexShrink:   0,
                  height:       TOPBAR_HEIGHT,
                  display:      "flex",
                  alignItems:   "center",
                  px:           2.5,
                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  cursor:       "pointer",
                  overflow:     "hidden",
                }}
              >
                <Box
                  component="img"
                  src={currentTheme ? logoLight : logoDark}
                  alt="Chalk It Up"
                  sx={{ height: 28, width: "auto", objectFit: "contain", flexShrink: 0 }}
                />
                <Typography noWrap sx={{ ml: 1.25, fontWeight: 700, fontSize: "0.95rem", letterSpacing: "-0.01em", color: "text.primary", userSelect: "none" }}>
                  Chalk It Up
                </Typography>
              </Box>

              <DrawerContent>
                <List sx={{ px: 0.5, pt: 2, pb: 2 }}>
                  {getRoleDrawerContent()}
                </List>
              </DrawerContent>
              <DrawerFooter>
                <Box sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                  <MobileDrawerActions />
                </Box>
                <Box sx={{ px: 2.5, py: 1.25, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                  <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem" }}>
                    © {new Date().getFullYear()} Chalk It Up · v1.0
                  </Typography>
                </Box>
              </DrawerFooter>
            </MuiDrawer>
          )}
          <Box sx={{ flex: 1, mt: 8, px: 1.5, pt: 2.5, pb: 2.5 }}>
            {children}
          </Box>
        </Box>
      </SidebarAccentProvider>
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  return (
    <SidebarAccentProvider minimized={isDrawerMinimized}>
      {/* Outer shell — page background fills the viewport */}
      <Box
        sx={{
          display:  "flex",
          height:   "100vh",
          overflow: "hidden",
          bgcolor:  "background.default",
          p:        "8px",
          gap:      "8px",
        }}
      >
        {/* ── Sidebar — floating panel ──────────────────────────────── */}
        {user?.role && (
          <StyledDrawer
            variant="permanent"
            open={isDrawerOpen}
            minimized={isDrawerMinimized}
            ismobile={0}
          >
            {/* Brand header — matches TopBar height so dividers align */}
            <Box
              onClick={() => router.push("/")}
              sx={{
                flexShrink:     0,
                height:         TOPBAR_HEIGHT,
                display:        "flex",
                alignItems:     "center",
                justifyContent: isDrawerMinimized ? "center" : "flex-start",
                px:             isDrawerMinimized ? 0 : 2.5,
                overflow:       "hidden",
                cursor:         "pointer",
                transition:     "padding 0.2s ease",
              }}
            >
              <Box
                component="img"
                src={currentTheme ? logoLight : logoDark}
                alt="Chalk It Up"
                sx={{ height: 28, width: "auto", objectFit: "contain", flexShrink: 0 }}
              />
              {!isDrawerMinimized && (
                <Typography
                  noWrap
                  sx={{
                    ml:            1.25,
                    fontWeight:    700,
                    fontSize:      "0.95rem",
                    letterSpacing: "-0.01em",
                    color:         "text.primary",
                    userSelect:    "none",
                  }}
                >
                  Chalk It Up
                </Typography>
              )}
            </Box>
            <Divider sx={{ mx: 1.5 }} />

            <DrawerContent>
              <List sx={{ px: 0.5, pt: 2, pb: 2 }}>
                {getRoleDrawerContent()}
              </List>
            </DrawerContent>

            <DrawerFooter>
              <Box sx={{ px: 2, py: 1.25, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
                <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.65rem", display: "block", textAlign: isDrawerMinimized ? "center" : "left" }}>
                  {isDrawerMinimized ? `© ${new Date().getFullYear()}` : `© ${new Date().getFullYear()} Chalk It Up · v1.0`}
                </Typography>
              </Box>
            </DrawerFooter>
          </StyledDrawer>
        )}

        {/* ── Right column: TopBar + scrollable content ────────────── */}
        <Box
          sx={{
            flex:          1,
            minWidth:      0,
            display:       "flex",
            flexDirection: "column",
            height:        "100%",
            overflow:      "hidden",
          }}
        >
          {user?.role && (
            <TopBar
              setTheme={setTheme}
              currentTheme={currentTheme}
              user={user}
              token={token}
              onMenuClick={handleDrawerToggle}
              topbarHeight={TOPBAR_HEIGHT}
            />
          )}

          {/* Scrollable page content */}
          <Box
            sx={{
              flex:      1,
              overflowY: "auto",
              px:        2.5,
              pt:        3,
              pb:        3,
              "&::-webkit-scrollbar":       { width: "6px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.15)", borderRadius: "3px" },
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </SidebarAccentProvider>
  );
};

export default withAuth(NavBarWDrawer);

// ─── Styled Components ─────────────────────────────────────────────────────────

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => !["open", "minimized", "ismobile"].includes(prop),
})(({ theme, open, minimized, ismobile }) => ({
  width:      open ? (minimized ? drawerMinWidth : drawerWidth) : 0,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing:  "border-box",
  transition: theme.transitions.create("width", {
    easing:    theme.transitions.easing.sharp,
    duration:  theme.transitions.duration.standard,
  }),

  "& .MuiDrawer-paper": {
    width: open ? (minimized ? drawerMinWidth : drawerWidth) : 0,
    transition: theme.transitions.create("width", {
      easing:   theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    overflowX:       "hidden",
    backgroundColor: theme.palette.background.paper,
    border:          `1px solid ${theme.palette.divider}`,
    boxShadow:       "none",
    // Desktop: relative in flex flow; Mobile: fixed overlay
    position:      ismobile ? "fixed" : "relative",
    top:           0,
    height:        ismobile ? "100vh" : "100%",
    zIndex:        ismobile ? theme.zIndex.drawer + 2 : "auto",
    display:       "flex",
    flexDirection: "column",
    borderRadius:  ismobile ? 0 : "12px",
  },
}));

// Scrollable section of the drawer
const DrawerContent = styled("div")({
  flexGrow:   1,
  overflowY:  "auto",
  overflowX:  "hidden",
  "&::-webkit-scrollbar":            { width: "4px" },
  "&::-webkit-scrollbar-track":      { background: "transparent" },
  "&::-webkit-scrollbar-thumb":      { background: "rgba(128,128,128,0.2)", borderRadius: "2px" },
  "&::-webkit-scrollbar-thumb:hover":{ background: "rgba(128,128,128,0.35)" },
});

// Pinned footer
const DrawerFooter = styled("div")({
  flexShrink: 0,
  marginTop:  "auto",
});
